import React from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchApi, makeResetCache, notifyError, notifyResponse, useCacheQuery } from 'gossamer-front-end';
import {
  AdminApplyRecurringEventCancellationRequest,
  AdminCancelEventRequest,
  AdminPreviewRecurringEventCancellationRequest,
  Event,
  EventExtendedView,
  PaymentIn,
  RecurringEventCancellationPreview,
} from 'cb-model';

type CancellationScope = 'this-event' | 'this-and-future' | 'entire-series';

interface EventDeleteConfirmModalProps {
  eventId: number;
  isRepeated: boolean;
  show: boolean;
  setShow: (val: boolean) => void;
}

const scopeLabel: Record<CancellationScope, string> = {
  'this-event': 'Cancel this event',
  'this-and-future': 'Cancel this and future events',
  'entire-series': 'Cancel entire series',
};

export const EventCancelConfirmModal = (props: EventDeleteConfirmModalProps) => {
  const [processing, setProcessing] = React.useState(false);
  const [scope, setScope] = React.useState<CancellationScope>('this-event');
  const [includeExceptions, setIncludeExceptions] = React.useState(false);
  const [preview, setPreview] = React.useState<RecurringEventCancellationPreview>();
  const dispatch = useDispatch();
  const resetCache = makeResetCache(dispatch);
  const navigate = useNavigate();
  const payments = useCacheQuery(PaymentIn, {
    where: {
      and: [{ fieldId: 'event_id', operator: 'eq', value: String(props.eventId) }],
    },
  });

  React.useEffect(() => {
    if (!props.show) return;
    setScope('this-event');
    setIncludeExceptions(false);
    setPreview(undefined);
  }, [props.show]);

  const invalidateEventCaches = () => {
    resetCache(Event.id);
    resetCache(Event.id, String(props.eventId));
    resetCache(EventExtendedView.id);
    resetCache(EventExtendedView.id, String(props.eventId));
  };

  const handleSingleCancel = async () => {
    const response = await fetchApi('POST', '/main/event-cancel', {
      eventId: props.eventId,
    } as AdminCancelEventRequest);
    invalidateEventCaches();
    notifyResponse(response);
    props.setShow(false);
    navigate('/admin/event/search');
  };

  const request = (): AdminPreviewRecurringEventCancellationRequest => ({
    eventId: props.eventId,
    scope: scope as Exclude<CancellationScope, 'this-event'>,
    includeExceptions,
  });

  const handlePreview = async () => {
    setProcessing(true);
    try {
      const response = await fetchApi('POST', '/main/recurring-event-cancellation-preview', request());
      const nextPreview = (response as { recurringEventCancellationPreview?: RecurringEventCancellationPreview })
        .recurringEventCancellationPreview;
      if (!nextPreview) throw new Error('The server did not return a cancellation preview');
      setPreview(nextPreview);
      notifyResponse(response);
    } catch (error) {
      notifyError(error, 'An error occurred while previewing the event cancellation');
    }
    setProcessing(false);
  };

  const handleApply = async () => {
    setProcessing(true);
    try {
      if (scope === 'this-event') {
        await handleSingleCancel();
        return;
      }
      const response = await fetchApi(
        'POST',
        '/main/recurring-event-cancellation-apply',
        request() as AdminApplyRecurringEventCancellationRequest,
      );
      invalidateEventCaches();
      notifyResponse(response);
      props.setShow(false);
      navigate('/admin/event/search');
    } catch (error) {
      notifyError(error, 'An error occurred while cancelling the event');
    } finally {
      setProcessing(false);
    }
  };

  const isSingleScope = !props.isRepeated || scope === 'this-event';
  return (
    <Modal
      show={props.show}
      onHide={() => props.setShow(false)}
      aria-labelledby="event-cancel-confirm-modal-title"
      size={props.isRepeated ? 'lg' : 'sm'}
      fullscreen="md-down"
    >
      <Modal.Header closeButton>
        <Modal.Title id="event-cancel-confirm-modal-title">Cancel event</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {props.isRepeated ? (
          <>
            <p>Choose which occurrences to cancel. Already cancelled events are never cancelled again.</p>
            {(Object.keys(scopeLabel) as CancellationScope[]).map((option) => (
              <Form.Check
                key={option}
                type="radio"
                name="event-cancellation-scope"
                id={`event-cancellation-scope-${option}`}
                className="mb-2"
                label={scopeLabel[option]}
                checked={scope === option}
                disabled={processing}
                onChange={() => {
                  setScope(option);
                  setPreview(undefined);
                }}
              />
            ))}
            {!isSingleScope && (
              <>
                <Form.Check
                  className="mt-3"
                  type="checkbox"
                  id="event-cancellation-include-exceptions"
                  label="Also cancel individually edited, rescheduled, or manually added exceptions"
                  checked={includeExceptions}
                  disabled={processing}
                  onChange={(event) => {
                    setIncludeExceptions(event.currentTarget.checked);
                    setPreview(undefined);
                  }}
                />
                <Alert variant="secondary" className="mt-3 mb-0">
                  Future includes the selected event and active occurrences starting at or after it. Exceptions are
                  protected by default.
                </Alert>
              </>
            )}
          </>
        ) : (
          <p>Are you sure you wish to cancel this event?</p>
        )}
        {isSingleScope && payments.data?.length > 0 && (
          <p className="mt-3">
            There are payments associated with this event. Cancelling would trigger an automatic full refund and you may
            be liable for transaction processing fees.
          </p>
        )}
        {preview && (
          <Alert variant="info" className="mt-3 mb-0">
            <strong>Preview</strong>
            <ul className="mb-0 mt-2">
              <li>{preview.total_event_count} event(s) in this series</li>
              <li>{preview.eligible_event_count} active event(s) will be cancelled</li>
              <li>{preview.excluded_event_count} protected or already cancelled event(s) excluded</li>
              <li>{preview.active_booking_count} active booking(s) will be cancelled</li>
              <li>{preview.payment_count} payment record(s) will be processed for refund</li>
            </ul>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => props.setShow(false)} disabled={processing}>
          Close
        </Button>
        {!isSingleScope && (
          <Button variant="secondary" onClick={handlePreview} disabled={processing}>
            {processing ? 'Working…' : 'Preview cancellation'}
          </Button>
        )}
        <Button variant="danger" onClick={handleApply} disabled={processing || (!isSingleScope && !preview)}>
          {processing ? 'Cancelling…' : isSingleScope ? 'Cancel event' : 'Apply previewed cancellation'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
